<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import { AnchorProps } from '~/components/ui/Anchor.vue'
import { ActionFields } from '~/components/cms/Action/actionHelpers'
import { BREAKPOINTS } from '~/config/common'

type Link = {
  text: string
  url: string
}

type sectionPanel = {
  title: string
  actions: ActionFields[]
}

type RootMenu = {
  id: string
  link: Link
  sectionPanel: sectionPanel[]
  sectionPanelLink?: AnchorProps
  menuStyle: 'sale' | undefined
}

type MenuProps = {
  rootMenuOpen: boolean
  rootMenu?: RootMenu[]
}

const props = defineProps<MenuProps>()
const emit = defineEmits(['rootMenuClose', 'rootMenuHover'])

const activePanel = ref('')
const BREAKPOINT_DESKTOP = BREAKPOINTS.desktopXL
const { storesUrl } = useSiteUrl()
const { localizeURL } = useSiteUrl()
const { initSiteNavigationSchema, setSiteNavigationLinks } = useSchemaOrg()
const intentToHover = ref(false)

if (import.meta.server) {
  initSiteNavigationSchema()
  setSiteNavigationLinks(
    (props.rootMenu ?? []).map((item) => ({
      name: item.link?.text || '',
      url: item.link?.url || '',
    })),
  )
}

watch(
  () => props.rootMenuOpen,
  (isOpen) => {
    if (!isOpen) {
      activePanel.value = ''
    }
  },
)

function trackSubMenuOpen(item: RootMenu, desktop?: boolean, location?: string) {
  pushEventToDataLayer('custom.menu.submenu', {
    group: desktop ? 'mega menu' : 'mobile menu',
    label: item?.link?.text,
    type: 'navigationItem',
    info: {
      location,
      url: item?.link?.url,
      element: document.getElementById(item?.id)?.tagName,
    },
  })
}

function handleRootMenuClick(item: RootMenu) {
  const desktop = window.innerWidth >= BREAKPOINT_DESKTOP
  if (!desktop) {
    activePanel.value = item?.id
  } else if (item?.link?.url) {
    window.location.href = localizeURL(item.link.url)
  }
  trackSubMenuOpen(item, desktop, 'sidebar')
}

const handleRootMenuHover = async (item: RootMenu) => {
  intentToHover.value = true
  // sleep to ensure user intends to hover and open menu, 300ms defined by UX
  await new Promise((resolve) => setTimeout(resolve, 300))
  if (!intentToHover.value) return
  const desktop = window.innerWidth >= BREAKPOINT_DESKTOP
  if (desktop) {
    emit('rootMenuHover')
    activePanel.value = item?.id
  }
  trackSubMenuOpen(item, desktop) // undefined location when hover
}

const handleRootMenuKeyUp = useDebounceFn((item: RootMenu) => {
  emit('rootMenuHover')
  activePanel.value = item?.id
  const desktop = window.innerWidth >= BREAKPOINT_DESKTOP
  trackSubMenuOpen(item, desktop, 'sidebar')
}, 300)

function handleMouseLeave() {
  if (window.innerWidth >= BREAKPOINT_DESKTOP) {
    activePanel.value = ''
  }
}

function closeMenuPanel(e: Event) {
  if (activePanel.value) {
    e.stopPropagation() // Don't let the root menu close on Esc.
  }
  activePanel.value = ''
}

function handleMenuClose(e: Event, skipEvent: boolean) {
  activePanel.value = ''
  if (!skipEvent) emit('rootMenuClose', e)
}
</script>

<template>
  <div class="menu" :class="{ 'menu--open': rootMenuOpen }" @keyup.escape="closeMenuPanel">
    <div class="menu__header-container">
      <div class="menu__header">
        <button
          class="menu__header-close"
          @click="$emit('rootMenuClose', $event)"
          :title="$t('screen_reader.buttons.close')"
        >
          <svg-icon name="mhj:cross" />
        </button>
      </div>
      <UserAccountButtonMode class="menu__header__my-account" :ga-options="{ eventName: 'custom.menu.link' }" />
    </div>

    <div class="menu__mask" v-show="!!activePanel" @click="closeMenuPanel"></div>

    <ul class="menu__root-links" @mouseleave="handleMouseLeave">
      <template v-for="item in rootMenu">
        <li v-if="item.link.text">
          <button
            data-testid="menu__root-link"
            aria-haspopup="true"
            :aria-expanded="activePanel == item?.id"
            :aria-controls="item?.id"
            @click="handleRootMenuClick(item)"
            @mouseover="handleRootMenuHover(item)"
            @mouseleave="intentToHover = false"
            @keyup.enter.prevent="handleRootMenuKeyUp(item)"
            @keyup.space.prevent="handleRootMenuKeyUp(item)"
            class="menu__root-link"
            :class="{ 'menu__root-link--sale': item?.menuStyle === 'sale' }"
          >
            {{ item?.link?.text }} <svg-icon name="mhj:arrow-carousel-right" />
          </button>
          <MenuPanels
            :id="item?.id"
            :title="item?.link?.text"
            :url="item?.link?.url"
            :panelLink="item?.sectionPanelLink"
            :panels="item?.sectionPanel"
            :open="item?.id === activePanel"
            @menu-panel-close="handleMenuClose"
          />
        </li>
      </template>
    </ul>

    <ul class="menu__nav-links">
      <li>
        <Wishlist
          :ga-options="{ eventName: 'custom.menu.link' }"
          class="menu__nav-link__wishlist"
          test-id="menu-wishlist"
          :label="$t('header.menu_drawer.buttons.wishlist')"
        />
      </li>
      <li>
        <Anchor
          url="/book-appointment"
          class="menu__nav-link__appointment"
          :ga-options="{
            eventName: 'custom.menu.link',
            label: $t('header.menu_drawer.buttons.book_appointment'),
            group: 'actions menu',
          }"
          :log-click="true"
        >
          <svg-icon name="mhj:book-appointment" />
          {{ $t('header.menu_drawer.buttons.book_appointment') }}
        </Anchor>
      </li>
      <li>
        <Anchor
          class="menu__nav-link__location"
          :url="storesUrl"
          :ga-options="{
            eventName: 'custom.menu.link',
            group: 'actions menu',
            label: 'Location',
          }"
          :log-click="true"
          :aria-label="$t('screen_reader.buttons.location')"
        >
          <svg-icon name="mhj:location-on" />
          {{ $t('header.menu_drawer.buttons.find_store') }}
        </Anchor>
      </li>
      <li>
        <LanguageSwitcher class="menu__nav-link__language-switcher" />
      </li>
    </ul>
  </div>
</template>

<style lang="postcss">
@import '~/assets/css/placeholders.css';

.menu {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  height: 100dvh;
  width: 100vw;
  background: var(--surface-light-00);
  padding-top: 0;
  display: none;
  overflow: auto;
  animation: slide-out-to-left 0.3s cubic-bezier(0.25, 0, 0.3, 1); /* closing effect */

  &.menu--open {
    z-index: var(--z-header);
    display: block;
    animation: slide-in-from-left 0.5s cubic-bezier(0.25, 0, 0.3, 1); /* opening effect */
    overscroll-behavior: contain;
  }

  button {
    cursor: pointer;
  }

  .menu__header-container {
    display: flex;
    justify-content: space-between;
    flex-direction: row-reverse;
    padding: var(--space-50);
    background-color: var(--surface-light-10);
    border-bottom: 1px solid var(--ui-neutral-20);
  }

  .menu__root-links {
    padding: var(--space-70) var(--space-60);
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .menu__root-link {
    @extend %heading-2;
    color: inherit;
    margin-bottom: 0;
    padding-block: 0.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    svg {
      height: var(--space-50);
      margin-right: var(--space-30);
    }
  }

  .menu__root-link--sale {
    color: var(--text-sale);
  }

  .menu__nav-links {
    margin: 0;
    border-top: 1px solid var(--border-neutral-20);
    padding: var(--space-70) var(--space-60);

    li {
      margin-bottom: var(--space-60);
    }

    .menu__nav-link__wishlist,
    .menu__nav-link__appointment,
    .menu__nav-link__location,
    .menu__nav-link__language-switcher {
      text-decoration: none;
      text-transform: unset;
      gap: var(--space-30);
      font-weight: bold;
      display: flex;
      align-items: center;
    }
  }

  .anchor {
    &:has(.anchor__arrow-icon) {
      display: flex;
      align-items: center;
    }
    .anchor__arrow-icon {
      display: unset;
      &.icons {
        height: var(--space-50);
      }
    }
  }
}

@media (--desktop) {
  .menu {
    display: block;
    position: static !important;
    height: auto;
    width: auto;
    padding: 0;
    animation: none;
    margin-top: 1px; /* Slight overflow for safe menu hover */

    .menu__header-container {
      display: none;
    }

    .menu__header {
      display: none;
    }

    .menu__root-links {
      flex-direction: row;
      padding: 0;
      gap: 2.25rem;
    }

    .menu__root-link {
      position: relative;
      font-size: var(--text-small);
      font-weight: normal;
      padding-block: var(--space-50);
      height: var(--header-menu-height);
      outline-offset: 0.1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: font-weight 0.8s;

      svg {
        display: none;
      }

      &:after {
        border-top: 2px solid currentColor;
        content: '';
        left: 0;
        position: absolute;
        right: 100%;
        bottom: 1rem;
        transition: right 0.4s cubic-bezier(0, 0.5, 0, 1);
      }

      &[aria-expanded='true'] {
        color: var(--text-link-hover);
        font-weight: bold;
        &:after {
          right: 0;
        }
      }
    }

    .menu__mask {
      position: fixed;
      z-index: 0;
      inset: var(--header-height) 0 0 0;
      background: var(--mask-bg-dark);
    }

    .menu__nav-links {
      display: none;
    }
  }
}

@media (--mobile-tablet-only) {
  .menu {
    clip-path: inset(0);
  }
  .menu,
  .menu__header {
    max-width: 28rem;
  }
}
</style>
